/**
 * Chat Redux Slice
 * Manages state for social media messaging including platforms, conversations,
 * messages, permissions, and unread counts
 */
import { createSlice } from '@reduxjs/toolkit';

import {
  getAllPermissionsByAccessType,
  getConversations,
  getFacebookConversations,
  getFacebookMessages,
  getInstagramConversations,
  getInstagramMessages,
  getMessages,
  getMyEffectivePermissions,
  getPlatforms,
  getSocialPermissions,
  getSocialPermissionsByRole,
  getSocialPermissionsByTeam,
  getSocialPermissionsByUser,
  getUnreadCount,
  markConversationAsRead,
  markMessageAsRead,
  sendMessage,
  setSocialPermissions,
  togglePermissionStatus,
} from './chatActions';

const initialState = {
  loading: {
    platform: false,
    permissions: false,
    savingPermissions: false,
    rolePermissions: false,
    teamPermissions: false,
    userPermissions: false,
    allUserPermissions: false,
    allTeamPermissions: false,
    allRolePermissions: false,
    myEffectivePermissions: false,
    conversations: false,
    facebookConversations: false,
    instagramConversations: false,
    messages: false,
    facebookMessages: false,
    instagramMessages: false,
    sendingMessage: false,
    unreadCount: false,
  },
  error: {
    platform: null,
    permissions: null,
    conversations: null,
    facebookConversations: null,
    instagramConversations: null,
    messages: null,
    facebookMessages: null,
    instagramMessages: null,
    unreadCount: null,
  },
  data: {
    platforms: [],
    permissions: {},
    rolePermissions: {},
    teamPermissions: {},
    userPermissions: {},
    allUserPermissions: [],
    allTeamPermissions: [],
    allRolePermissions: [],
    myEffectivePermissions: null,
    conversations: [],
    conversationsTotal: 0,
    facebookConversations: [],
    facebookPagination: {
      hasNextPage: false,
      hasPreviousPage: false,
      cursors: {
        before: null,
        after: null,
      },
    },
    instagramConversations: [],
    instagramPagination: {
      hasNextPage: false,
      hasPreviousPage: false,
      cursors: {
        before: null,
        after: null,
      },
    },
    messages: [],
    messagesTotal: 0,
    facebookMessages: [],
    facebookMessagesUserId: null,
    facebookMessagesPagination: {
      hasNextPage: false,
      hasPreviousPage: false,
      cursors: {
        before: null,
        after: null,
      },
    },
    instagramMessages: [],
    instagramMessagesUserId: null,
    instagramMessagesPagination: {
      hasNextPage: false,
      hasPreviousPage: false,
      cursors: {
        before: null,
        after: null,
      },
    },
    totalUnreadCount: 0,
  },
};

const chatSlice = createSlice({
  name: 'chatSlice',
  initialState,
  reducers: {
    /**
     * Update local permissions for a specific target (user, team, or role)
     * @param {Object} payload - { targetId, permissions }
     */
    updateLocalPermissions: (state, { payload }) => {
      const { targetId, permissions } = payload;
      state.data.permissions[targetId] = permissions;
    },
    /**
     * Clear permissions for a specific target or all permissions
     * @param {Object} payload - { targetId } or empty object to clear all
     */
    clearPermissions: (state, { payload }) => {
      const { targetId } = payload;
      if (targetId) {
        delete state.data.permissions[targetId];
      } else {
        state.data.permissions = {};
      }
    },
    /**
     * Add an incoming real-time message from socket to the messages array
     * Prevents duplicates by checking messageId and _id
     * @param {Object} payload - Message object from socket event
     */
    addIncomingMessage: (state, { payload }) => {
      const message = payload;

      // Only add if message belongs to current conversation
      if (message && message.conversationId) {
        const platform = message.platform?.toLowerCase();

        // Route to correct array based on platform
        if (platform === 'facebook') {
          // Check if message already exists in Facebook messages
          const exists = state.data.facebookMessages.some(
            (m) =>
              m._id === message._id ||
              m.messageId === message.messageId ||
              m.id === message._id
          );

          // console.log(
          //   '🔵 Facebook - exists:',
          //   exists,
          //   'current array length:',
          //   state.data.facebookMessages.length
          // );
          if (!exists) {
            state.data.facebookMessages.push(message);
            // console.log(
            //   '✅ Added to facebookMessages array. New length:',
            //   state.data.facebookMessages.length
            // );
          } else {
            // console.log('⚠️ Message already exists in facebookMessages, skipping');
          }
        } else if (platform === 'instagram') {
          // Check if message already exists in Instagram messages
          const exists = state.data.instagramMessages.some(
            (m) =>
              m._id === message._id ||
              m.messageId === message.messageId ||
              m.id === message._id
          );

          // console.log(
          //   '🔵 Instagram - exists:',
          //   exists,
          //   'current array length:',
          //   state.data.instagramMessages.length
          // );
          if (!exists) {
            state.data.instagramMessages.push(message);
            // console.log(
            //   '✅ Added to instagramMessages array. New length:',
            //   state.data.instagramMessages.length
            // );
          }
        } else {
          const exists = state.data.messages.some(
            (m) => m._id === message._id || m.messageId === message.messageId
          );

          console.log(
            '🔵 Other platform - exists:',
            exists,
            'current array length:',
            state.data.messages.length
          );
          if (!exists) {
            state.data.messages.push(message);
            state.data.messagesTotal += 1;
          } else {
            // console.log('⚠️ Message already exists in messages, skipping');
          }
        }
      } else {
        //Invalid message or missing conversationId
      }
    },
    /**
     * Update the read status of a specific message
     * @param {Object} payload - { messageId, isRead }
     */
    updateMessageReadStatus: (state, { payload }) => {
      const { messageId, isRead } = payload;
      // Update the read status of a specific message
      const messageIndex = state.data.messages.findIndex(
        (m) => m._id === messageId || m.messageId === messageId
      );
      if (messageIndex !== -1) {
        state.data.messages[messageIndex].isRead = isRead;
      }
    },

    clearFacebookMessages: (state) => {
      state.data.facebookMessages = [];
      state.data.facebookMessagesUserId = null;
      state.data.facebookMessagesPagination = {
        hasNextPage: false,
        hasPreviousPage: false,
        cursors: {
          before: null,
          after: null,
        },
      };
    },

    clearInstagramMessages: (state) => {
      state.data.instagramMessages = [];
      state.data.instagramMessagesUserId = null;
      state.data.instagramMessagesPagination = {
        hasNextPage: false,
        hasPreviousPage: false,
        cursors: {
          before: null,
          after: null,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlatforms.pending, (state) => {
        state.loading.platform = true;
        state.error.platform = null;
      })
      .addCase(getPlatforms.fulfilled, (state, { payload }) => {
        state.loading.platform = false;
        state.error.platform = null;
        state.data.platforms = payload.platforms || [];
      })
      .addCase(getPlatforms.rejected, (state, { payload }) => {
        state.loading.platform = false;
        state.error.platform = payload;
        state.data.platforms = [];
      })
      .addCase(getSocialPermissions.pending, (state) => {
        state.loading.permissions = true;
        state.error.permissions = null;
      })
      .addCase(getSocialPermissions.fulfilled, (state, { payload }) => {
        state.loading.permissions = false;
        state.error.permissions = null;
        const { targetId, permissions } = payload;
        state.data.permissions[targetId] = permissions;
      })
      .addCase(getSocialPermissions.rejected, (state, { payload }) => {
        state.loading.permissions = false;
        state.error.permissions = payload;
      })
      .addCase(getSocialPermissionsByRole.pending, (state) => {
        state.loading.rolePermissions = true;
        state.error.permissions = null;
      })
      .addCase(getSocialPermissionsByRole.fulfilled, (state, { payload }) => {
        state.loading.rolePermissions = false;
        state.error.permissions = null;
        const { roleName, data } = payload;
        state.data.rolePermissions[roleName] = data;
      })
      .addCase(getSocialPermissionsByRole.rejected, (state, { payload }) => {
        state.loading.rolePermissions = false;
        state.error.permissions = payload;
      })
      .addCase(getSocialPermissionsByTeam.pending, (state) => {
        state.loading.teamPermissions = true;
        state.error.permissions = null;
      })
      .addCase(getSocialPermissionsByTeam.fulfilled, (state, { payload }) => {
        state.loading.teamPermissions = false;
        state.error.permissions = null;
        const { teamId, data } = payload;
        state.data.teamPermissions[teamId] = data;
      })
      .addCase(getSocialPermissionsByTeam.rejected, (state, { payload }) => {
        state.loading.teamPermissions = false;
        state.error.permissions = payload;
      })
      .addCase(getSocialPermissionsByUser.pending, (state) => {
        state.loading.userPermissions = true;
        state.error.permissions = null;
      })
      .addCase(getSocialPermissionsByUser.fulfilled, (state, { payload }) => {
        state.loading.userPermissions = false;
        state.error.permissions = null;
        const { userId, data } = payload;
        state.data.userPermissions[userId] = data;
      })
      .addCase(getSocialPermissionsByUser.rejected, (state, { payload }) => {
        state.loading.userPermissions = false;
        state.error.permissions = payload;
      })
      .addCase(setSocialPermissions.pending, (state) => {
        state.loading.savingPermissions = true;
        state.error.permissions = null;
      })
      .addCase(setSocialPermissions.fulfilled, (state, { payload }) => {
        state.loading.savingPermissions = false;
        state.error.permissions = null;
        const { targetId, permissions } = payload;
        if (permissions) {
          state.data.permissions[targetId] = permissions;
        }
      })
      .addCase(setSocialPermissions.rejected, (state, { payload }) => {
        state.loading.savingPermissions = false;
        state.error.permissions = payload;
      })
      .addCase(getAllPermissionsByAccessType.pending, (state, { meta }) => {
        const { accessType } = meta.arg;
        if (accessType === 'user') {
          state.loading.allUserPermissions = true;
        } else if (accessType === 'team') {
          state.loading.allTeamPermissions = true;
        } else if (accessType === 'role') {
          state.loading.allRolePermissions = true;
        }
        state.error.permissions = null;
      })
      .addCase(getAllPermissionsByAccessType.fulfilled, (state, { payload }) => {
        const { accessType, data } = payload;
        if (accessType === 'user') {
          state.loading.allUserPermissions = false;
          state.data.allUserPermissions = data || [];
        } else if (accessType === 'team') {
          state.loading.allTeamPermissions = false;
          state.data.allTeamPermissions = data || [];
        } else if (accessType === 'role') {
          state.loading.allRolePermissions = false;
          state.data.allRolePermissions = data || [];
        }
        state.error.permissions = null;
      })
      .addCase(getAllPermissionsByAccessType.rejected, (state, { payload, meta }) => {
        const { accessType } = meta.arg;
        if (accessType === 'user') {
          state.loading.allUserPermissions = false;
        } else if (accessType === 'team') {
          state.loading.allTeamPermissions = false;
        } else if (accessType === 'role') {
          state.loading.allRolePermissions = false;
        }
        state.error.permissions = payload;
      })
      .addCase(togglePermissionStatus.fulfilled, (state, { payload }) => {
        const { permissionId, isActive, accessType } = payload;
        if (accessType === 'role') {
          const index = state.data.allRolePermissions.findIndex((p) => p._id === permissionId);
          if (index !== -1) {
            state.data.allRolePermissions[index].isActive = isActive;
          }
        } else if (accessType === 'team') {
          const index = state.data.allTeamPermissions.findIndex((p) => p._id === permissionId);
          if (index !== -1) {
            state.data.allTeamPermissions[index].isActive = isActive;
          }
        } else if (accessType === 'user') {
          const index = state.data.allUserPermissions.findIndex((p) => p._id === permissionId);
          if (index !== -1) {
            state.data.allUserPermissions[index].isActive = isActive;
          }
        }
      })
      .addCase(getMyEffectivePermissions.pending, (state) => {
        state.loading.myEffectivePermissions = true;
        state.error.permissions = null;
      })
      .addCase(getMyEffectivePermissions.fulfilled, (state, { payload }) => {
        state.loading.myEffectivePermissions = false;
        state.data.myEffectivePermissions = payload;
      })
      .addCase(getMyEffectivePermissions.rejected, (state, { payload }) => {
        state.loading.myEffectivePermissions = false;
        state.error.permissions = payload;
      })
      .addCase(getConversations.pending, (state) => {
        state.loading.conversations = true;
        state.error.conversations = null;
      })
      .addCase(getConversations.fulfilled, (state, { payload }) => {
        state.loading.conversations = false;
        state.data.conversations = payload.conversations || [];
        state.data.conversationsTotal = payload.total || 0;
      })
      .addCase(getConversations.rejected, (state, { payload }) => {
        state.loading.conversations = false;
        state.error.conversations = payload;
        state.data.conversations = [];
        state.data.conversationsTotal = 0;
      })
      .addCase(getFacebookConversations.pending, (state) => {
        state.loading.facebookConversations = true;
        state.error.facebookConversations = null;
      })
      .addCase(getFacebookConversations.fulfilled, (state, { payload }) => {
        state.loading.facebookConversations = false;
        const { data, pagination, append } = payload;

        if (append) {
          state.data.facebookConversations = [...state.data.facebookConversations, ...data];
        } else {
          state.data.facebookConversations = data;
        }

        state.data.facebookPagination = {
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
          cursors: {
            before: pagination.cursors?.before || null,
            after: pagination.cursors?.after || null,
          },
        };
      })
      .addCase(getFacebookConversations.rejected, (state, { payload }) => {
        state.loading.facebookConversations = false;
        state.error.facebookConversations = payload;
      })
      .addCase(getFacebookMessages.pending, (state) => {
        state.loading.facebookMessages = true;
        state.error.facebookMessages = null;
      })
      .addCase(getFacebookMessages.fulfilled, (state, { payload }) => {
        state.loading.facebookMessages = false;
        const { data, pagination, direction, userId } = payload;

        if (state.data.facebookMessagesUserId !== userId) {
          state.data.facebookMessagesUserId = userId;
          state.data.facebookMessages = data;
        } else if (direction === 'initial') {
          state.data.facebookMessages = data;
        } else if (direction === 'prepend') {
          state.data.facebookMessages = [...data, ...state.data.facebookMessages];
        } else if (direction === 'append') {
          state.data.facebookMessages = [...state.data.facebookMessages, ...data];
        }

        state.data.facebookMessagesPagination = {
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
          cursors: {
            before: pagination.cursors?.before || null,
            after: pagination.cursors?.after || null,
          },
        };
      })
      .addCase(getFacebookMessages.rejected, (state, { payload }) => {
        state.loading.facebookMessages = false;
        state.error.facebookMessages = payload;
      })
      .addCase(getInstagramConversations.pending, (state) => {
        state.loading.instagramConversations = true;
        state.error.instagramConversations = null;
      })
      .addCase(getInstagramConversations.fulfilled, (state, { payload }) => {
        state.loading.instagramConversations = false;
        const { data, pagination, append } = payload;

        if (append) {
          state.data.instagramConversations = [...state.data.instagramConversations, ...data];
        } else {
          state.data.instagramConversations = data;
        }

        state.data.instagramPagination = {
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
          cursors: {
            before: pagination.cursors?.before || null,
            after: pagination.cursors?.after || null,
          },
        };
      })
      .addCase(getInstagramConversations.rejected, (state, { payload }) => {
        state.loading.instagramConversations = false;
        state.error.instagramConversations = payload;
      })
      .addCase(getInstagramMessages.pending, (state) => {
        state.loading.instagramMessages = true;
        state.error.instagramMessages = null;
      })
      .addCase(getInstagramMessages.fulfilled, (state, { payload }) => {
        state.loading.instagramMessages = false;
        const { data, pagination, direction, userId } = payload;

        if (state.data.instagramMessagesUserId !== userId) {
          state.data.instagramMessagesUserId = userId;
          state.data.instagramMessages = data;
        } else if (direction === 'initial') {
          state.data.instagramMessages = data;
        } else if (direction === 'prepend') {
          state.data.instagramMessages = [...data, ...state.data.instagramMessages];
        } else if (direction === 'append') {
          state.data.instagramMessages = [...state.data.instagramMessages, ...data];
        }

        state.data.instagramMessagesPagination = {
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
          cursors: {
            before: pagination.cursors?.before || null,
            after: pagination.cursors?.after || null,
          },
        };
      })
      .addCase(getInstagramMessages.rejected, (state, { payload }) => {
        state.loading.instagramMessages = false;
        state.error.instagramMessages = payload;
      })
      .addCase(getMessages.pending, (state, { meta }) => {
        const offset = meta.arg?.offset || 0;
        if (offset === 0) {
          state.loading.messages = true;
        }
        state.error.messages = null;
      })
      .addCase(getMessages.fulfilled, (state, { payload }) => {
        state.loading.messages = false;
        const offset = payload.offset || 0;
        const newMessages = payload.messages || [];

        if (offset === 0) {
          state.data.messages = newMessages;
        } else {
          const existingIds = new Set(state.data.messages.map((m) => m._id));
          const uniqueNewMessages = newMessages.filter((m) => !existingIds.has(m._id));
          state.data.messages = [...uniqueNewMessages, ...state.data.messages];
        }

        state.data.messagesTotal = payload.total || 0;
      })
      .addCase(getMessages.rejected, (state, { payload }) => {
        state.loading.messages = false;
        state.error.messages = payload;
        state.data.messages = [];
        state.data.messagesTotal = 0;
      })
      .addCase(sendMessage.pending, (state) => {
        state.loading.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        state.loading.sendingMessage = false;
        if (payload?.message) {
          const message = payload.message;
          const platform = message.platform?.toLowerCase();

          if (platform === 'facebook') {
            const exists = state.data.facebookMessages.some(
              (m) =>
                m._id === message._id ||
                m.messageId === message.messageId ||
                m.id === message._id
            );
            if (!exists) {
              state.data.facebookMessages.push(message);
            }
          } else if (platform === 'instagram') {
            const exists = state.data.instagramMessages.some(
              (m) =>
                m._id === message._id ||
                m.messageId === message.messageId ||
                m.id === message._id
            );
            if (!exists) {
              state.data.instagramMessages.push(message);
            }
          } else {
            const exists = state.data.messages.some(
              (m) =>
                m._id === message._id ||
                m.messageId === message.messageId ||
                m.id === message._id
            );
            if (!exists) {
              state.data.messages.push(message);
              state.data.messagesTotal += 1;
            }
          }
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.loading.sendingMessage = false;
      })
      .addCase(markMessageAsRead.fulfilled, (state, { payload }) => {
        const { messageId } = payload;
        const messageIndex = state.data.messages.findIndex(
          (m) => m._id === messageId || m.messageId === messageId || m.id === messageId
        );
        if (messageIndex !== -1) {
          state.data.messages[messageIndex].isRead = true;
        }
      })
      .addCase(markConversationAsRead.fulfilled, (state, { payload }) => {
        const { conversationId } = payload;
        // Mark all messages in the current conversation as read
        state.data.messages = state.data.messages.map((message) => {
          if (message.conversationId === conversationId) {
            return { ...message, isRead: true };
          }
          return message;
        });
      })
      .addCase(getUnreadCount.pending, (state) => {
        state.loading.unreadCount = true;
        state.error.unreadCount = null;
      })
      .addCase(getUnreadCount.fulfilled, (state, { payload }) => {
        state.loading.unreadCount = false;
        state.data.totalUnreadCount = payload.unreadCount || 0;
      })
      .addCase(getUnreadCount.rejected, (state, { payload }) => {
        state.loading.unreadCount = false;
        state.error.unreadCount = payload;
      });
  },
});

export const {
  updateLocalPermissions,
  clearPermissions,
  addIncomingMessage,
  updateMessageReadStatus,
  clearFacebookMessages,
  clearInstagramMessages,
} = chatSlice.actions;

export const selectPlatforms = (state) => state.chat.data.platforms;
export const selectPlatformsLoading = (state) => state.chat.loading.platform;
export const selectPermissions = (state) => state.chat.data.permissions;
export const selectRolePermissions = (state) => state.chat.data.rolePermissions;
export const selectTeamPermissions = (state) => state.chat.data.teamPermissions;
export const selectUserPermissions = (state) => state.chat.data.userPermissions;
export const selectAllUserPermissions = (state) => state.chat.data.allUserPermissions;
export const selectAllTeamPermissions = (state) => state.chat.data.allTeamPermissions;
export const selectAllRolePermissions = (state) => state.chat.data.allRolePermissions;
export const selectPermissionsLoading = (state) => state.chat.loading.permissions;
export const selectRolePermissionsLoading = (state) => state.chat.loading.rolePermissions;
export const selectTeamPermissionsLoading = (state) => state.chat.loading.teamPermissions;
export const selectUserPermissionsLoading = (state) => state.chat.loading.userPermissions;
export const selectAllUserPermissionsLoading = (state) =>
  state.chat.loading.allUserPermissions;
export const selectAllTeamPermissionsLoading = (state) =>
  state.chat.loading.allTeamPermissions;
export const selectAllRolePermissionsLoading = (state) =>
  state.chat.loading.allRolePermissions;
export const selectSavingPermissions = (state) => state.chat.loading.savingPermissions;
export const selectMyEffectivePermissions = (state) => state.chat.data.myEffectivePermissions;
export const selectMyEffectivePermissionsLoading = (state) =>
  state.chat.loading.myEffectivePermissions;
export const selectConversations = (state) => state.chat.data.conversations;
export const selectConversationsTotal = (state) => state.chat.data.conversationsTotal;
export const selectConversationsLoading = (state) => state.chat.loading.conversations;
export const selectFacebookConversations = (state) => state.chat.data.facebookConversations;
export const selectFacebookPagination = (state) => state.chat.data.facebookPagination;
export const selectFacebookConversationsLoading = (state) =>
  state.chat.loading.facebookConversations;
export const selectFacebookConversationsError = (state) =>
  state.chat.error.facebookConversations;
export const selectMessages = (state) => state.chat.data.messages;
export const selectMessagesTotal = (state) => state.chat.data.messagesTotal;
export const selectMessagesLoading = (state) => state.chat.loading.messages;
export const selectSendingMessage = (state) => state.chat.loading.sendingMessage;
export const selectTotalUnreadCount = (state) => state.chat.data.totalUnreadCount;
export const selectUnreadCountLoading = (state) => state.chat.loading.unreadCount;
export const selectUnreadCountError = (state) => state.chat.error.unreadCount;
export const selectFacebookMessages = (state) => state.chat.data.facebookMessages;
export const selectFacebookMessagesUserId = (state) => state.chat.data.facebookMessagesUserId;
export const selectFacebookMessagesPagination = (state) =>
  state.chat.data.facebookMessagesPagination;
export const selectFacebookMessagesLoading = (state) => state.chat.loading.facebookMessages;
export const selectFacebookMessagesError = (state) => state.chat.error.facebookMessages;
export const selectInstagramConversations = (state) => state.chat.data.instagramConversations;
export const selectInstagramPagination = (state) => state.chat.data.instagramPagination;
export const selectInstagramConversationsLoading = (state) =>
  state.chat.loading.instagramConversations;
export const selectInstagramConversationsError = (state) =>
  state.chat.error.instagramConversations;
export const selectInstagramMessages = (state) => state.chat.data.instagramMessages;
export const selectInstagramMessagesUserId = (state) =>
  state.chat.data.instagramMessagesUserId;
export const selectInstagramMessagesPagination = (state) =>
  state.chat.data.instagramMessagesPagination;
export const selectInstagramMessagesLoading = (state) => state.chat.loading.instagramMessages;
export const selectInstagramMessagesError = (state) => state.chat.error.instagramMessages;

export default chatSlice.reducer;
